import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Wallet as WalletIcon, Receipt, FileText, TrendingUp, ArrowRight, Download,
  AlertCircle, CheckCircle2, Clock,
} from "lucide-react";

/* Billing (rebuilt) — /vendor/billing

   The old page (leads × ₹2 + messages × ₹0.5 + Pay button) is gone. This
   page now reflects the real billing model:

     • Prepaid wallet balance (shortcut to /vendor/wallet)
     • Per-campaign billing status (trial / paid / pending / paused)
     • Invoice history from GET /payments/invoices (each with an eventual
       "Download PDF" button — currently a placeholder toast per user's
       chosen defer plan).

   No more static per-period math.
*/

const rupees = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }).format(
    Number(n || 0)
  );
const paiseR = (p) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }).format(
    Number(p || 0) / 100
  );

const CAMPAIGN_STATUS_META = {
  active: { label: "Active", cls: "ag-pill ag-pill--active", icon: CheckCircle2 },
  paused: { label: "Paused", cls: "ag-pill ag-pill--pending", icon: AlertCircle },
  pending_payment: { label: "Awaiting payment", cls: "ag-pill ag-pill--pending", icon: Clock },
  trial_ended: { label: "Trial ended", cls: "ag-pill ag-pill--suspended", icon: AlertCircle },
  paid_trial: { label: "Trial", cls: "ag-pill ag-pill--info", icon: Clock },
};

export default function BillingPage() {
  const [wallet, setWallet] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [w, inv, camps] = await Promise.all([
          api.walletSummary().catch(() => ({ data: { balance_paise: 0 } })),
          api.listInvoices().catch(() => ({ data: [] })),
          api.listCampaigns().catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        setWallet(w.data);
        setInvoices(inv.data || []);
        setCampaigns(camps.data || []);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const paidCount = campaigns.filter((c) => c.payment_status === "paid").length;
  const trialCount = campaigns.filter((c) => c.is_trial).length;
  const pausedCount = campaigns.filter((c) => c.status === "paused").length;

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div>
        <span className="mkt-tape">Billing</span>
        <h1 className="ag-page-title mt-2">
          Money <span className="mkt-highlight">stuff</span>
        </h1>
        <p className="text-sm text-black/70 mt-2 max-w-2xl">
          Track your prepaid wallet, per-campaign billing status, and download GST-compliant invoices.
        </p>
      </div>

      {/* Overview grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5" data-testid="billing-wallet-card">
          <div className="flex items-center gap-2 text-black">
            <WalletIcon className="h-4 w-4" />
            <p className="text-[11px] font-bold uppercase tracking-widest">Wallet</p>
          </div>
          {loading ? (
            <Skeleton className="h-10 w-24 mt-2" />
          ) : (
            <p className="font-display text-3xl tabular-nums mt-2" data-testid="billing-wallet-balance">
              {paiseR(wallet?.balance_paise || 0)}
            </p>
          )}
          <Link to="/vendor/wallet" className="inline-flex items-center gap-1 text-xs font-bold text-black mt-3 uppercase tracking-wider underline decoration-[var(--graffiti-yellow)] decoration-4 underline-offset-2">
            Top up <ArrowRight className="h-3 w-3" />
          </Link>
        </Card>

        <Card className="p-5" data-testid="billing-campaigns-card">
          <div className="flex items-center gap-2 text-black">
            <TrendingUp className="h-4 w-4" />
            <p className="text-[11px] font-bold uppercase tracking-widest">Campaigns</p>
          </div>
          <p className="font-display text-3xl tabular-nums mt-2">{campaigns.length}</p>
          <div className="flex flex-wrap gap-1.5 mt-2 text-[10px]">
            <span className="ag-pill ag-pill--active">{paidCount} paid</span>
            {trialCount > 0 && <span className="ag-pill ag-pill--info">{trialCount} trial</span>}
            {pausedCount > 0 && <span className="ag-pill ag-pill--pending">{pausedCount} paused</span>}
          </div>
        </Card>

        <Card className="p-5" data-testid="billing-invoices-card">
          <div className="flex items-center gap-2 text-black">
            <Receipt className="h-4 w-4" />
            <p className="text-[11px] font-bold uppercase tracking-widest">Invoices</p>
          </div>
          <p className="font-display text-3xl tabular-nums mt-2">{invoices.length}</p>
          <p className="text-xs text-black/60 mt-1">GST invoices for paid campaigns.</p>
        </Card>
      </div>

      {/* Per-campaign billing status */}
      <Card className="p-5 md:p-6" data-testid="billing-campaign-list">
        <div className="flex items-center gap-2 border-b-2 border-black pb-3 mb-3">
          <TrendingUp className="h-5 w-5" />
          <h2 className="font-heading text-base font-bold uppercase tracking-wide">Campaign billing status</h2>
        </div>
        {loading ? (
          <Skeleton className="h-32 w-full" />
        ) : campaigns.length === 0 ? (
          <div className="ag-empty">No campaigns yet.</div>
        ) : (
          <ul className="divide-y divide-black/10">
            {campaigns.map((c) => {
              const key = c.status === "active" && c.is_trial ? "paid_trial" : c.status;
              const meta = CAMPAIGN_STATUS_META[key] || CAMPAIGN_STATUS_META.active;
              return (
                <li key={c.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-black truncate">{c.name}</p>
                    <p className="text-xs text-black/60 mt-0.5">
                      {c.payment_status ? `${c.payment_status}` : "—"}
                      {" · "}
                      cap {c.lead_cap ?? "n/a"} leads
                      {c.status === "paused" && c.pause_reason ? ` · ${c.pause_reason.replace(/_/g, " ")}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={meta.cls}>
                      <meta.icon className="h-3 w-3" /> {meta.label}
                    </span>
                    {(c.status === "pending_payment" || c.status === "trial_ended") && (
                      <Link to={`/vendor/checkout/${c.id}`}>
                        <button className="ag-btn-ghost !text-xs !py-1.5 !px-3">Pay</button>
                      </Link>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {/* Invoice history */}
      <Card className="p-5 md:p-6" data-testid="billing-invoice-history">
        <div className="flex items-center gap-2 border-b-2 border-black pb-3 mb-3">
          <FileText className="h-5 w-5" />
          <h2 className="font-heading text-base font-bold uppercase tracking-wide">Invoice history</h2>
        </div>
        {loading ? (
          <Skeleton className="h-24 w-full" />
        ) : invoices.length === 0 ? (
          <div className="ag-empty">
            No invoices yet — your first invoice appears after your first paid campaign.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left">Invoice #</th>
                  <th className="text-left">Date</th>
                  <th className="text-left">Campaign</th>
                  <th className="text-right">Base</th>
                  <th className="text-right">GST</th>
                  <th className="text-right">Total</th>
                  <th className="w-16"></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id}>
                    <td className="py-2.5 font-bold">{inv.invoice_number}</td>
                    <td className="py-2.5 text-black/70">
                      {new Date(inv.issued_at).toLocaleDateString()}
                    </td>
                    <td className="py-2.5 text-black/70 truncate max-w-[16ch]">{inv.campaign_name || "—"}</td>
                    <td className="py-2.5 text-right tabular-nums">{rupees(inv.base_amount)}</td>
                    <td className="py-2.5 text-right tabular-nums">{rupees(inv.gst_amount)}</td>
                    <td className="py-2.5 text-right tabular-nums font-bold">{rupees(inv.total_amount)}</td>
                    <td className="py-2.5 text-right">
                      <button
                        onClick={async () => {
                          const { toast } = await import("sonner");
                          try {
                            const r = await api.downloadInvoicePdf(inv.invoice_number);
                            // Trigger a save-as with the invoice number as filename.
                            const blob = new Blob([r.data], { type: "application/pdf" });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `${inv.invoice_number}.pdf`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            setTimeout(() => URL.revokeObjectURL(url), 500);
                          } catch (err) {
                            const msg =
                              err?.response?.status === 404
                                ? "This invoice's PDF isn't available yet — please refresh in a minute or contact support."
                                : "Couldn't download the PDF. Please try again.";
                            toast.error(msg);
                          }
                        }}
                        className="ag-btn-ghost !text-[10px] !py-1 !px-2"
                        data-testid={`billing-invoice-download-${inv.id}`}
                      >
                        <Download className="h-3 w-3" /> PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
